import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Gift } from 'lucide-react';
import { useAuth } from '../../../src/contexts/AuthContext';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import {
  podeEditarPrograma,
  podeGerirIndicacoes,
  podePagarRecompensas,
  podeUsarIndicacoes,
} from '../../../src/lib/indicacoes/permissoes';
import { formatarMoeda } from '../../../src/lib/indicacoes/rotulos';
import { kpisDoMes } from '../../../src/services/indicacoes';
import type { IndicacaoKpisMes } from '../../../types';
import PessoasTab from './PessoasTab';
import IndicacoesTab from './IndicacoesTab';
import RecompensasTab from './RecompensasTab';
import ProgramaTab from './ProgramaTab';
import { CLASSE_CARTAO } from './estilos';

type Aba = 'pessoas' | 'indicacoes' | 'recompensas' | 'programa';

/**
 * Programa de Indicações — rota /crm/indicacoes (#6196).
 * Desenho: docs/superpowers/specs/2026-10-19-programa-indicacoes-design.md.
 * Quem vê o quê é decidido no banco (fn_indicacao_*); aqui só se escolhe quais abas aparecem.
 */
const IndicacoesView: React.FC = () => {
  const { profile } = useAuth();
  const [aba, setAba] = useState<Aba>('pessoas');
  const [kpis, setKpis] = useState<IndicacaoKpisMes | null>(null);
  const [versao, setVersao] = useState(0);
  const usa = podeUsarIndicacoes(profile);
  const gere = podeGerirIndicacoes(profile);
  const paga = podePagarRecompensas(profile);

  useEffect(() => {
    if (!usa) return;
    kpisDoMes()
      .then(setKpis)
      .catch(e => console.error('Erro ao carregar os números de indicações:', e));
  }, [usa, versao]);

  // AuthContext destrava a tela mesmo sem perfil (timeout de 8s) — não assumir que existe.
  if (!profile) return <LoadingSpinner label="Carregando..." />;
  if (!usa) return <Navigate to="/crm" replace />;

  const mudou = () => setVersao(v => v + 1);
  const abas: { id: Aba; rotulo: string }[] = [
    { id: 'pessoas', rotulo: 'Pessoas' },
    { id: 'indicacoes', rotulo: 'Indicações' },
    ...(paga ? [{ id: 'recompensas' as const, rotulo: 'Recompensas' }] : []),
    { id: 'programa', rotulo: 'Programa' },
  ];
  const cartoes: { rotulo: string; valor: string; detalhe?: string }[] = [
    { rotulo: 'Abordagens no mês', valor: String(kpis?.abordagens ?? '–') },
    { rotulo: 'Indicações no mês', valor: String(kpis?.indicacoes ?? '–') },
    { rotulo: 'Aprovadas no mês', valor: String(kpis?.aprovadas ?? '–') },
    ...(paga
      ? [{
          rotulo: 'Recompensas a pagar',
          valor: kpis?.a_pagar_valor != null ? formatarMoeda(kpis.a_pagar_valor) : '–',
          detalhe: kpis ? `${kpis.a_pagar_qtd ?? 0} a pagar · ${kpis.a_definir_qtd ?? 0} sem valor` : undefined,
        }]
      : []),
  ];

  return (
    <div className="space-y-4 max-w-7xl mx-auto px-2 sm:px-0">
      <div className="flex items-start gap-3">
        <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
          <Gift size={22} />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white font-poppins">Indicações</h1>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            {gere ? 'Abordagens, indicações e recompensas de todo o time.' : 'As abordagens e indicações que você registrou.'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cartoes.map(c => (
          <div key={c.rotulo} className={`${CLASSE_CARTAO} px-4 py-3`}>
            <p className="text-xs text-gray-500 dark:text-slate-400">{c.rotulo}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{c.valor}</p>
            {c.detalhe && <p className="text-[11px] text-gray-400 dark:text-slate-500">{c.detalhe}</p>}
          </div>
        ))}
      </div>

      <div className="flex gap-1 border-b border-gray-200 dark:border-slate-700 overflow-x-auto">
        {abas.map(a => (
          <button
            key={a.id}
            onClick={() => setAba(a.id)}
            className={`px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${
              aba === a.id
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200'
            }`}
          >
            {a.rotulo}
          </button>
        ))}
      </div>

      {aba === 'pessoas' && <PessoasTab versao={versao} onMudou={mudou} gere={gere} />}
      {aba === 'indicacoes' && <IndicacoesTab versao={versao} />}
      {aba === 'recompensas' && paga && <RecompensasTab versao={versao} onMudou={mudou} />}
      {aba === 'programa' && <ProgramaTab podeEditar={podeEditarPrograma(profile)} />}
    </div>
  );
};

export default IndicacoesView;
